import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../lib/firebase/config';

// ── Types ────────────────────────────────────────────────────────────────────

export interface SoundboardSound {
  id: string;
  name: string;
  emoji?: string;
  storagePath: string;
  downloadUrl: string;
  contentType: string;
  sizeBytes: number;
  order: number;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: Timestamp | null;
  // When true, this sound is reserved for automated event triggers and is
  // hidden from the manual soundboard launcher.
  automationOnly?: boolean;
}

export interface SoundboardTrigger {
  soundId: string;
  soundName: string;
  triggeredBy: string;
  triggeredByName: string;
  triggeredAt: Timestamp | null;
  // When set, only the listener whose `currentUser.uid === targetUserId`
  // should play this sound. When null/absent, every soundboard listener
  // plays it (the original "broadcast to all" behavior).
  targetUserId?: string | null;
  // Random nonce so two clicks of the same sound at nearly the same instant
  // (server timestamp resolution is not always sub-millisecond) still register
  // as distinct snapshot updates and trigger playback again.
  nonce?: string;
}

/** Lightweight user shape used by the soundboard target-picker. */
export interface SoundboardUser {
  uid: string;
  displayName: string;
  email: string;
  photoURL: string | null;
  isMasterAdmin: boolean;
}

/**
 * Broadcast "stop everything" trigger doc. Mirrors `SoundboardTrigger` but
 * carries no sound — every recipient simply pauses any currently-playing
 * audio. `targetUserId` follows the same semantics as the play trigger:
 * null/absent = stop on all devices, set = only that user pauses.
 */
export interface SoundboardStopTrigger {
  stoppedBy: string;
  stoppedByName: string;
  stoppedAt: Timestamp | null;
  targetUserId?: string | null;
  nonce?: string;
}

// ── Firestore paths ──────────────────────────────────────────────────────────

const SOUNDS_COLLECTION = 'soundboardSounds';
const STATE_COLLECTION = 'soundboardState';
const TRIGGER_DOC_ID = 'current';
const STOP_DOC_ID = 'stop';

const STORAGE_FOLDER = 'soundboard';

// Per-user playback prefs (volume, muted) — keyed by uid in localStorage.
const VOLUME_KEY = 'soundboard:volume';
const MUTED_KEY = 'soundboard:muted';
// Per-device target for button presses: 'all' (broadcast to every soundboard
// user), 'me' (play locally only, no Firestore write), or 'user' (broadcast
// to a single targeted user — uid stored separately under TARGET_USER_KEY).
const PLAY_MODE_KEY = 'soundboard:playMode';
// Per-device chosen recipient when playMode === 'user'.
const TARGET_USER_KEY = 'soundboard:targetUserId';
// Per-device "Do Not Disturb" — when true, the listener ignores incoming
// broadcasts and event sounds. Local "Me" plays still work because they're
// user-initiated and bypass the listener path entirely.
const DND_KEY = 'soundboard:dnd';

// Event-sound assignments live alongside other settings so Master Admins
// (the only writers of `/settings`) own the mapping.
const EVENT_SOUNDS_DOC = ['settings', 'soundboardEvents'] as const;

// ── Active-audio registry ────────────────────────────────────────────────────
//
// Module-level Set tracking every <audio> element currently playing on this
// device. The Listener registers any audio it spawns; the Launcher can
// stop them all on demand. Audio elements auto-unregister on `ended` so the
// set doesn't grow without bound.

const activeAudios = new Set<HTMLAudioElement>();
type AudioStateListener = (count: number) => void;
const audioStateListeners = new Set<AudioStateListener>();

const notifyAudioState = () => {
  for (const cb of audioStateListeners) {
    try {
      cb(activeAudios.size);
    } catch {
      /* noop */
    }
  }
};

/** Register a freshly spawned audio so `stopAllSounds()` can pause it later. */
export function registerActiveAudio(audio: HTMLAudioElement): void {
  activeAudios.add(audio);
  notifyAudioState();
  const cleanup = () => {
    activeAudios.delete(audio);
    audio.removeEventListener('ended', cleanup);
    audio.removeEventListener('pause', cleanup);
    audio.removeEventListener('error', cleanup);
    notifyAudioState();
  };
  audio.addEventListener('ended', cleanup);
  audio.addEventListener('pause', cleanup);
  audio.addEventListener('error', cleanup);
}

/** Pause every active audio on this device immediately. */
export function stopAllSounds(): void {
  for (const audio of Array.from(activeAudios)) {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      /* noop */
    }
  }
  activeAudios.clear();
  notifyAudioState();
}

/** Subscribe to changes in the count of currently-playing audio elements. */
export function subscribeToActiveAudioCount(cb: AudioStateListener): () => void {
  audioStateListeners.add(cb);
  cb(activeAudios.size);
  return () => {
    audioStateListeners.delete(cb);
  };
}

// ── Event sound types ────────────────────────────────────────────────────────

export type SoundboardEventKey =
  | 'newSale'
  | 'submittedForReview'
  | 'pushedToShopify'
  | 'pushedToEbay'
  | 'newReview'
  | 'newChat';

export interface EventSoundConfig {
  // Legacy single sound. Kept for backwards compatibility / as a fallback.
  soundId: string | null;
  // Pool of sounds for this event. When more than one is set, a random one is
  // chosen each time the event fires.
  soundIds?: string[];
  enabled: boolean;
}

export type SoundboardEventSounds = Partial<Record<SoundboardEventKey, EventSoundConfig>>;

/**
 * Resolve the effective pool of sound ids for an event config, preferring the
 * new `soundIds` array and falling back to the legacy single `soundId`.
 */
export function resolveEventSoundIds(cfg?: EventSoundConfig | null): string[] {
  if (!cfg) return [];
  if (cfg.soundIds && cfg.soundIds.length > 0) return cfg.soundIds;
  return cfg.soundId ? [cfg.soundId] : [];
}

export const EVENT_DEFINITIONS: Array<{ key: SoundboardEventKey; label: string; description: string }> = [
  {
    key: 'newSale',
    label: 'New sale',
    description: 'Plays when a new sale is added to the sales collection.',
  },
  {
    key: 'submittedForReview',
    label: 'Submitted for review',
    description: 'Plays only for the user who sent the product entry for review.',
  },
  {
    key: 'pushedToShopify',
    label: 'Published to CCO (Shopify)',
    description: 'Plays only for the user who pushed the item live to Shopify.',
  },
  {
    key: 'pushedToEbay',
    label: 'Published to eBay',
    description: 'Plays only for the user who pushed the item live to eBay.',
  },
  {
    key: 'newReview',
    label: 'New 5★ review',
    description: 'Plays when a customer submits a survey response containing a 5-star rating. (Requires survey-response access.)',
  },
  {
    key: 'newChat',
    label: 'Customer chatting with us',
    description: 'Plays this sound when a customer first starts a chat; follow-up messages use the original subtle chime. (Requires live-chat access.)',
  },
];

// ── Sound list (subscribe) ───────────────────────────────────────────────────

export function subscribeToSounds(
  onChange: (sounds: SoundboardSound[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, SOUNDS_COLLECTION), orderBy('order', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      const sounds = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SoundboardSound, 'id'>) }));
      onChange(sounds);
    },
    (err) => {
      console.error('subscribeToSounds error:', err);
      onError?.(err as Error);
    },
  );
}

// ── Upload + create ──────────────────────────────────────────────────────────

export interface UploadSoundParams {
  file: File;
  name: string;
  emoji?: string;
  uploadedBy: string;
  uploadedByName: string;
  /** Used to place this sound at the end of the existing list. */
  existingCount: number;
  /** Reserve this sound for automated events only (hidden from the launcher). */
  automationOnly?: boolean;
}

const MAX_SOUND_BYTES = 5 * 1024 * 1024; // 5 MB

export async function uploadSound({
  file,
  name,
  emoji,
  uploadedBy,
  uploadedByName,
  existingCount,
  automationOnly,
}: UploadSoundParams): Promise<SoundboardSound> {
  if (!file) throw new Error('No file provided');
  if (file.size > MAX_SOUND_BYTES) {
    throw new Error(`File too large (max ${MAX_SOUND_BYTES / 1024 / 1024} MB)`);
  }
  if (!file.type.startsWith('audio/')) {
    throw new Error('File must be an audio file (mp3, wav, ogg, etc.)');
  }

  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? (crypto as Crypto & { randomUUID: () => string }).randomUUID()
      : `sb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const ext = file.name.includes('.') ? file.name.split('.').pop() : 'mp3';
  const path = `${STORAGE_FOLDER}/${id}.${ext}`;

  const sRef = storageRef(storage, path);
  await uploadBytes(sRef, file, { contentType: file.type });
  const downloadUrl = await getDownloadURL(sRef);

  const sound: SoundboardSound = {
    id,
    name: name.trim() || file.name.replace(/\.[^.]+$/, ''),
    emoji: emoji?.trim() || '',
    storagePath: path,
    downloadUrl,
    contentType: file.type,
    sizeBytes: file.size,
    order: existingCount,
    uploadedBy,
    uploadedByName,
    uploadedAt: serverTimestamp() as unknown as Timestamp,
    automationOnly: !!automationOnly,
  };

  await setDoc(doc(db, SOUNDS_COLLECTION, id), sound);
  return sound;
}

// ── Update / delete ──────────────────────────────────────────────────────────

export async function updateSound(
  id: string,
  updates: Partial<Pick<SoundboardSound, 'name' | 'emoji' | 'automationOnly'>>,
): Promise<void> {
  await setDoc(doc(db, SOUNDS_COLLECTION, id), updates, { merge: true });
}

export async function deleteSound(sound: SoundboardSound): Promise<void> {
  // Delete the storage object first; if that fails we still want to remove
  // the doc to keep the UI clean, but log the orphan for cleanup.
  try {
    if (sound.storagePath) {
      await deleteObject(storageRef(storage, sound.storagePath));
    }
  } catch (err) {
    console.warn('Failed to delete soundboard storage object:', sound.storagePath, err);
  }
  await deleteDoc(doc(db, SOUNDS_COLLECTION, sound.id));
}

export async function reorderSounds(orderedIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    batch.set(doc(db, SOUNDS_COLLECTION, id), { order: index }, { merge: true });
  });
  await batch.commit();
}

// ── Trigger / listen ─────────────────────────────────────────────────────────

export async function triggerSound(
  sound: SoundboardSound,
  triggeredBy: string,
  triggeredByName: string,
  targetUserId?: string | null,
): Promise<void> {
  const trigger: SoundboardTrigger = {
    soundId: sound.id,
    soundName: sound.name,
    triggeredBy,
    triggeredByName,
    triggeredAt: serverTimestamp() as unknown as Timestamp,
    targetUserId: targetUserId || null,
    nonce: Math.random().toString(36).slice(2, 12),
  };
  await setDoc(doc(db, STATE_COLLECTION, TRIGGER_DOC_ID), trigger);
}

export function subscribeToTrigger(
  onTrigger: (trigger: SoundboardTrigger) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, STATE_COLLECTION, TRIGGER_DOC_ID),
    (snap) => {
      if (!snap.exists()) return;
      onTrigger(snap.data() as SoundboardTrigger);
    },
    (err) => {
      console.error('subscribeToTrigger error:', err);
      onError?.(err as Error);
    },
  );
}

/**
 * Broadcasts a "stop all sounds" event. Every soundboard listener (or just
 * the targeted one) will pause any audio currently playing on its device.
 */
export async function triggerStop(
  stoppedBy: string,
  stoppedByName: string,
  targetUserId?: string | null,
): Promise<void> {
  const trigger: SoundboardStopTrigger = {
    stoppedBy,
    stoppedByName,
    stoppedAt: serverTimestamp() as unknown as Timestamp,
    targetUserId: targetUserId || null,
    nonce: Math.random().toString(36).slice(2, 12),
  };
  await setDoc(doc(db, STATE_COLLECTION, STOP_DOC_ID), trigger);
}

export function subscribeToStopTrigger(
  onStop: (trigger: SoundboardStopTrigger) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, STATE_COLLECTION, STOP_DOC_ID),
    (snap) => {
      if (!snap.exists()) return;
      onStop(snap.data() as SoundboardStopTrigger);
    },
    (err) => {
      console.error('subscribeToStopTrigger error:', err);
      onError?.(err as Error);
    },
  );
}

/**
 * Subscribes to the list of users who can be targeted by a directed sound.
 * That set is the union of:
 *   • users with `soundboardAccess: true` on their user doc, and
 *   • Master Admins (regardless of the toggle, since they implicitly have
 *     access in the launcher).
 *
 * We run two queries because Firestore doesn't support OR filters, then
 * merge by uid client-side. The list is sorted alphabetically by display
 * name for the picker UI.
 */
export function subscribeToSoundboardUsers(
  onChange: (users: SoundboardUser[]) => void,
  onError?: (err: Error) => void,
): () => void {
  let withAccess = new Map<string, SoundboardUser>();
  let masters = new Map<string, SoundboardUser>();

  const emit = () => {
    const merged = new Map<string, SoundboardUser>();
    for (const [uid, u] of withAccess) merged.set(uid, u);
    for (const [uid, u] of masters) {
      const existing = merged.get(uid);
      merged.set(uid, { ...(existing ?? u), isMasterAdmin: true });
    }
    const list = Array.from(merged.values()).sort((a, b) =>
      (a.displayName || a.email).localeCompare(b.displayName || b.email),
    );
    onChange(list);
  };

  const toUser = (snap: { id: string; data: () => Record<string, unknown> }): SoundboardUser => {
    const d = snap.data();
    const roles = Array.isArray(d.roles) ? (d.roles as string[]) : [];
    return {
      uid: snap.id,
      displayName: typeof d.displayName === 'string' ? d.displayName : '',
      email: typeof d.email === 'string' ? d.email : '',
      photoURL: typeof d.photoURL === 'string' ? d.photoURL : null,
      isMasterAdmin: roles.includes('Master Admin'),
    };
  };

  const handleErr = (err: Error) => {
    console.error('subscribeToSoundboardUsers error:', err);
    onError?.(err);
  };

  const u1 = onSnapshot(
    query(collection(db, 'users'), where('soundboardAccess', '==', true)),
    (snap) => {
      withAccess = new Map(snap.docs.map((d) => [d.id, toUser(d)]));
      emit();
    },
    handleErr,
  );

  const u2 = onSnapshot(
    query(collection(db, 'users'), where('roles', 'array-contains', 'Master Admin')),
    (snap) => {
      masters = new Map(snap.docs.map((d) => [d.id, toUser(d)]));
      emit();
    },
    handleErr,
  );

  return () => {
    u1();
    u2();
  };
}

// ── Local prefs ──────────────────────────────────────────────────────────────

export function getStoredVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (!raw) return 0.7;
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return 0.7;
    return Math.max(0, Math.min(1, parsed));
  } catch {
    return 0.7;
  }
}

export function setStoredVolume(v: number): void {
  try {
    localStorage.setItem(VOLUME_KEY, String(Math.max(0, Math.min(1, v))));
  } catch {
    /* noop */
  }
}

export function getStoredMuted(): boolean {
  try {
    return localStorage.getItem(MUTED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setStoredMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTED_KEY, muted ? 'true' : 'false');
  } catch {
    /* noop */
  }
}

export type SoundboardPlayMode = 'all' | 'me' | 'user';

export function getStoredPlayMode(): SoundboardPlayMode {
  try {
    const raw = localStorage.getItem(PLAY_MODE_KEY);
    if (raw === 'me' || raw === 'user') return raw;
    return 'all';
  } catch {
    return 'all';
  }
}

export function setStoredPlayMode(mode: SoundboardPlayMode): void {
  try {
    localStorage.setItem(PLAY_MODE_KEY, mode);
  } catch {
    /* noop */
  }
}

export function getStoredTargetUserId(): string | null {
  try {
    const raw = localStorage.getItem(TARGET_USER_KEY);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

export function setStoredTargetUserId(uid: string | null): void {
  try {
    if (uid) localStorage.setItem(TARGET_USER_KEY, uid);
    else localStorage.removeItem(TARGET_USER_KEY);
  } catch {
    /* noop */
  }
}

export function getStoredDnd(): boolean {
  try {
    return localStorage.getItem(DND_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setStoredDnd(value: boolean): void {
  try {
    localStorage.setItem(DND_KEY, value ? 'true' : 'false');
  } catch {
    /* noop */
  }
}

/**
 * Plays a sound directly on the current device, without broadcasting to other
 * soundboard users. Honors the per-device mute toggle and stored volume.
 */
export function playSoundLocally(sound: SoundboardSound): void {
  if (getStoredMuted()) return;
  try {
    const audio = new Audio(sound.downloadUrl);
    audio.volume = getStoredVolume();
    audio.crossOrigin = 'anonymous';
    registerActiveAudio(audio);
    audio.play().catch((err) => {
      console.warn('Soundboard local playback blocked or failed:', err);
    });
  } catch (err) {
    console.warn('Soundboard local playback error:', err);
  }
}

// ── Event sound mapping ──────────────────────────────────────────────────────

export function subscribeToEventSounds(
  onChange: (sounds: SoundboardEventSounds) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, EVENT_SOUNDS_DOC[0], EVENT_SOUNDS_DOC[1]),
    (snap) => {
      if (!snap.exists()) {
        onChange({});
        return;
      }
      onChange((snap.data() || {}) as SoundboardEventSounds);
    },
    (err) => {
      console.error('subscribeToEventSounds error:', err);
      onError?.(err as Error);
    },
  );
}

export async function setEventSound(
  eventKey: SoundboardEventKey,
  cfg: EventSoundConfig,
): Promise<void> {
  await setDoc(
    doc(db, EVENT_SOUNDS_DOC[0], EVENT_SOUNDS_DOC[1]),
    { [eventKey]: cfg },
    { merge: true },
  );
}
